import { useTranslation } from "react-i18next";
import type { BroadcastAsset } from "../../lib/broadcastApi";
import {
  clampAnimMs,
  type AnimSpec,
  type AnimType,
} from "./animation/enterExit";
import type { PanelConfig } from "./panel/Panel";

const field =
  "w-full rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100";

const clampNum = (v: string, lo: number, hi: number, fb: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fb;
};

/** Per-widget Panel (background, radius, border, text shadow); null = legacy. */
export function PanelEditor({
  panel,
  library,
  onChange,
}: {
  panel: PanelConfig | null | undefined;
  library: BroadcastAsset[];
  onChange: (next: PanelConfig | null) => void;
}) {
  const { t } = useTranslation("broadcast");
  const set = (patch: Partial<PanelConfig>) =>
    onChange({ ...(panel ?? { background: "none" }), ...patch });
  return (
    <fieldset className="space-y-2" data-testid="drawer-panel">
      <legend className="text-xs text-slate-300">
        {t("control.panel.title")}
      </legend>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="drawer-panel-enabled"
          checked={!!panel}
          onChange={(e) =>
            onChange(e.target.checked ? { background: "none" } : null)
          }
        />
        {t("control.panel.enabled")}
      </label>
      {panel && (
        <>
          <label className="block text-xs text-slate-300">
            {t("control.panel.background")}
            <select
              className={field}
              data-testid="drawer-panel-background"
              value={panel.background}
              onChange={(e) => {
                const background = e.target.value as PanelConfig["background"];
                // No panel behind the text: the halo is on by default
                // (still toggleable) so it stays legible over busy video.
                set(
                  background === "none"
                    ? { background, text_shadow: true }
                    : { background },
                );
              }}
            >
              <option value="none">{t("control.panel.bg.none")}</option>
              <option value="color">{t("control.panel.bg.color")}</option>
              <option value="image">{t("control.panel.bg.image")}</option>
            </select>
          </label>
          {panel.background === "color" && (
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-slate-300">
                {t("control.panel.color")}
                <input
                  type="color"
                  data-testid="drawer-panel-color"
                  className="h-8 w-full"
                  value={panel.color ?? "#000000"}
                  onChange={(e) => set({ color: e.target.value })}
                />
              </label>
              <label className="block text-xs text-slate-300">
                {t("control.panel.opacity")}: {panel.opacity ?? 1}
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  data-testid="drawer-panel-opacity"
                  className="w-full"
                  value={panel.opacity ?? 1}
                  onChange={(e) =>
                    set({ opacity: clampNum(e.target.value, 0, 1, 1) })
                  }
                />
              </label>
            </div>
          )}
          {panel.background === "image" && (
            <label className="block text-xs text-slate-300">
              {t("control.panel.image")}
              <select
                className={field}
                data-testid="drawer-panel-asset"
                value={panel.asset_id ?? ""}
                onChange={(e) => set({ asset_id: e.target.value || null })}
              >
                <option value="">{t("control.panel.noImage")}</option>
                {library.map((a) => (
                  <option key={a.asset_id} value={a.asset_id}>
                    {t(`control.assets.kinds.${a.kind}`)} {a.width}x{a.height}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block text-xs text-slate-300">
            {t("control.panel.radius")}: {panel.radius_px ?? 0}
            <input
              type="number"
              min={0}
              max={64}
              data-testid="drawer-panel-radius"
              className={field}
              value={panel.radius_px ?? 0}
              onChange={(e) =>
                set({ radius_px: clampNum(e.target.value, 0, 64, 0) })
              }
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="drawer-panel-border-enabled"
              checked={!!panel.border?.enabled}
              onChange={(e) =>
                set({
                  border: {
                    ...(panel.border ?? {}),
                    enabled: e.target.checked,
                  },
                })
              }
            />
            {t("control.panel.border")}
          </label>
          {panel.border?.enabled && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="color"
                aria-label={t("control.panel.borderColor")}
                data-testid="drawer-panel-border-color"
                className="h-8 w-full"
                value={panel.border.color ?? "#ffffff"}
                onChange={(e) =>
                  set({ border: { ...panel.border!, color: e.target.value } })
                }
              />
              <input
                type="number"
                min={0}
                max={8}
                aria-label={t("control.panel.borderWidth")}
                data-testid="drawer-panel-border-width"
                className={field}
                value={panel.border.width_px ?? 1}
                onChange={(e) =>
                  set({
                    border: {
                      ...panel.border!,
                      width_px: clampNum(e.target.value, 0, 8, 1),
                    },
                  })
                }
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="drawer-panel-shadow"
              checked={!!panel.text_shadow}
              onChange={(e) => set({ text_shadow: e.target.checked })}
            />
            {t("control.panel.textShadow")}
          </label>
          {panel.background === "none" && (
            <p
              className="text-xs text-slate-300"
              data-testid="drawer-panel-shadow-hint"
            >
              {t("control.panel.noPanelHint")}
            </p>
          )}
        </>
      )}
    </fieldset>
  );
}

const FROM: NonNullable<AnimSpec["from"]>[] = [
  "top",
  "bottom",
  "left",
  "right",
];

/** Enter or exit animation of one widget; none = null (legacy: instant). */
export function AnimEditor({
  which,
  value,
  onChange,
}: {
  which: "enter" | "exit";
  value: AnimSpec | null | undefined;
  onChange: (next: AnimSpec | null) => void;
}) {
  const { t } = useTranslation("broadcast");
  const type: AnimType = value?.type ?? "none";
  return (
    <fieldset className="space-y-2" data-testid={`drawer-${which}`}>
      <legend className="text-xs text-slate-300">
        {t(`control.anim.${which}`)}
      </legend>
      <div className="grid grid-cols-3 gap-2">
        <select
          aria-label={t("control.anim.type")}
          data-testid={`drawer-${which}-type`}
          className={field}
          value={type}
          onChange={(e) => {
            const next = e.target.value as AnimType;
            if (next === "none") return onChange(null);
            onChange({
              type: next,
              ...(next === "slide" ? { from: value?.from ?? "bottom" } : {}),
              duration_ms: value?.duration_ms ?? 500,
            });
          }}
        >
          {(["none", "fade", "slide", "scale"] as AnimType[]).map((k) => (
            <option key={k} value={k}>
              {t(`control.anim.types.${k}`)}
            </option>
          ))}
        </select>
        {type === "slide" ? (
          <select
            aria-label={t("control.anim.from")}
            data-testid={`drawer-${which}-from`}
            className={field}
            value={value?.from ?? "bottom"}
            onChange={(e) =>
              onChange({ ...value!, from: e.target.value as AnimSpec["from"] })
            }
          >
            {FROM.map((f) => (
              <option key={f} value={f}>
                {t(`control.anim.froms.${f}`)}
              </option>
            ))}
          </select>
        ) : (
          <span />
        )}
        {type !== "none" && (
          <input
            type="number"
            min={0}
            max={1500}
            step={50}
            aria-label={t("control.anim.duration")}
            data-testid={`drawer-${which}-duration`}
            className={field}
            value={clampAnimMs(value?.duration_ms ?? 500)}
            onChange={(e) =>
              onChange({
                ...value!,
                duration_ms: clampAnimMs(Number(e.target.value)),
              })
            }
          />
        )}
      </div>
    </fieldset>
  );
}
